import { FailedToGetData, H2, Loading } from 'src/components/_commons';
import { setAlertResponse } from 'src/store';
import { sse } from '@/app-config';
import { sseEvents } from '@monorepo/helpers';
import { TelephonicBody } from './subcomponents/_TelephonicBody';
import { telephonicServices, userServices } from 'src/services';
import { typeTelephonicTerritory, typeUser } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useGetCurrentUser, useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';
import { UsersSelector } from 'src/components/_commons';

export const AdminsTelephonic = () => {
    const [isLoading1, setIsLoading1] = useState(true);
    const [isLoading2, setIsLoading2] = useState(true);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [selectedOption, setSelectedOption] = useState(1);
    const [showOpened, setShowOpened] = useState(true);
    const [territories, setTerritories] = useState<typeTelephonicTerritory[] | null>(null);
    const [userRefreshCounter, setUserRefreshCounter] = useState(0);
    const [users, setUsers] = useState<typeUser[] | null>(null);
    const [usersToShow, setUsersToShow] = useState<typeUser[]>([]);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const isMobile = useGetIsMobile();
    const user = useGetCurrentUser();

    const territoriesToShow: typeTelephonicTerritory[] = useMemo(() => {
        if (!territories || !users) {
            return [];
        }
        const sample = usersToShow.length < users.length ?
            [...territories.filter(t => t.assignments.some(a => usersToShow.map(u => u.id).includes(a)))]
            :
            [...territories]
        ;
        if (selectedOption === 1) {
            sample.sort((t1, t2) => parseInt(t1.territoryNumber) - parseInt(t2.territoryNumber));
        } else if (selectedOption === 2) {
            sample.sort((t1, t2) => new Date(t1.last ?? '').getTime() - new Date(t2.last ?? 0).getTime());
        } else if (selectedOption === 3) {
            sample.sort((t1, t2) =>
                t1.leftRel === '-' ?
                    parseFloat(t2.leftRel ?? '0')
                    :
                    t2.leftRel === '-' ?
                        parseFloat(t1.leftRel ?? '0')
                        :
                        parseFloat(t2.leftRel ?? '0') - parseFloat(t1.leftRel ?? '0')
                )
            ;
        } else {
            sample.sort((t1, t2) => (t2.total ?? 0) - (t1.total ?? 0));
        }
        return sample;
    }, [selectedOption, territories, users, usersToShow]);

    useEffect(() => {
        telephonicServices.GetGlobalStatistics().then(response => {
            setIsLoading1(false);
            if (!response.success || !response.data?.territoriesTableData) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setTerritories(response.data.territoriesTableData);
        });
    }, [refreshCounter]);

    useEffect(() => {
        userServices.GetAll().then(response => {
            setIsLoading2(false);
            if (!response.success || !response.data?.users) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setUsers(response.data.users);
        });
    }, [userRefreshCounter]);

    useEffect(() => {
        const handler = (e: MessageEvent) => {
            const data = JSON.parse(e.data);
            if (data.congregation !== user.congregation) {
                return;
            }
            setRefreshCounter(x => x + 1);
        };
        sse.addEventListener(sseEvents.telephonicChange, handler);
        return () => {
            sse.removeEventListener(sseEvents.telephonicChange, handler);
        };
    }, [user.congregation]);

    useEffect(() => {
        const handler = (e: MessageEvent) => {
            const data = JSON.parse(e.data);
            if (data.congregation !== user.congregation) {
                return;
            }
            setUserRefreshCounter(x => x + 1);
        };
        sse.addEventListener(sseEvents.userChange, handler);
        return () => {
            sse.removeEventListener(sseEvents.userChange, handler);
        };
    }, [user.congregation]);

    if (isLoading1 || isLoading2) {
        return <Loading mt={'60px'} mb={'20px'} />;
    }

    if (!territories || !users) {
        return <FailedToGetData />;
    }

    return (
        <>
        <H2 title={'ADMINISTRACIÓN TELEFÓNICA'} />
        <div className={'my-5'}>
            {!!territories.length &&
                <div className={'row'}>
                    <div className={`col-md-auto card ${isDarkMode ? 'bg-dark text-white' : ''} pt-4 ${isMobile ? 'mb-2' : ''}`} style={{ backgroundColor: '#f6f6f8' }}>
                        <div className={'form-check'}>
                            <label className={`form-check-label ${isDarkMode ? 'text-white' : ''}`} htmlFor={'frd1'}>
                                Ordenar por territorio
                            </label>
                            <input className={'form-check-input ms-0 me-2'}
                                type={'radio'} name={'frd'} id={'frd1'} value={1}
                                onChange={e => setSelectedOption(parseInt(e.target.value))}
                                checked={selectedOption === 1}
                            />
                        </div>
                        <div className={'form-check'}>
                            <label className={`form-check-label ${isDarkMode ? 'text-white' : ''}`} htmlFor={'frd2'}>
                                Ordenar por última vez
                            </label>
                            <input className={'form-check-input ms-0 me-2'}
                                type={'radio'} name={'frd'} id={'frd2'} value={2}
                                onChange={e => setSelectedOption(parseInt(e.target.value))}
                                checked={selectedOption === 2}
                            />
                        </div>
                        <div className={'form-check'}>
                            <label className={`form-check-label ${isDarkMode ? 'text-white' : ''}`} htmlFor={'frd3'}>
                                Ordenar por % de completado
                            </label>
                            <input className={'form-check-input ms-0 me-2'}
                                type={'radio'} name={'frd'} id={'frd3'} value={3}
                                onChange={e => setSelectedOption(parseInt(e.target.value))}
                                checked={selectedOption === 3}
                            />
                        </div>
                        <div className={'form-check'}>
                            <label className={`form-check-label ${isDarkMode ? 'text-white' : ''}`} htmlFor={'frd4'}>
                                Ordenar por tamaño
                            </label>
                            <input className={'form-check-input ms-0 me-2'}
                                type={'radio'} name={'frd'} id={'frd4'} value={4}
                                onChange={e => setSelectedOption(parseInt(e.target.value))}
                                checked={selectedOption === 4}
                            />
                        </div>
                        <div className={'form-check my-2'}>
                            <input className={'form-check-input ms-0 me-2'} type={'checkbox'} id={'fcc1'}
                                value={''} checked={showOpened} onChange={() => setShowOpened(x => !x)}
                            />
                            <label className={`form-check-label ${isDarkMode ? 'text-white' : ''}`} htmlFor={'fcc1'}>
                                Ver Abiertos
                            </label>
                        </div>
                    </div>
                    <div className={'col-md-6'}>
                        <UsersSelector
                            setUsersToShow={setUsersToShow}
                            users={users}
                            usersToShow={usersToShow}
                        />
                    </div>
                </div>
            }
        </div>
        <div className={'overflow-auto'}>
            <table className={`table ${isDarkMode ? 'table-dark' : ''} table-hover`}>
                <thead>
                    <tr>
                        <th className={''}>TERR</th>
                        <th className={''}>ASIGNADO A</th>
                        <th className={'text-center'}>ASIGNAR</th>
                        <th className={'text-center'}>ESTADO</th>
                        <th className={'text-center'}>TOTAL</th>
                        <th className={'text-center'}>QUEDAN</th>
                        <th className={'text-center'}>NO ABON.</th>
                        <th className={'text-center'}>ÚLT. VEZ</th>
                        <th className={'text-center'}>ÚLT. RESETEADO</th>
                        <th className={'text-center'}>RESETEAR</th>
                    </tr>
                </thead>
                <tbody>
                    {!!territoriesToShow.length ?
                        territoriesToShow.map(territory =>
                            <TelephonicBody key={territory.territoryNumber}
                                showOpened={showOpened}
                                t={territory}
                                users={users}
                            />
                        )
                        :
                        <tr>
                            <td colSpan={isMobile ? 3 : 7}> No hay datos </td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    </>
    );
}
